import React, { useCallback, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "components/CustomButtons/Button";
import { asCurrency } from "../../../utils";
import { Box, TextField } from "@material-ui/core";
import Loading from "../../common/Loading";
import { HelpOutline } from "@material-ui/icons";
import { REPORT_ITEM_SUB_TYPE, REPORT_ITEM_TYPE } from "../../../constants";

const useStyles = makeStyles(theme => ({
  borderBottom: {
    borderBottom: "1px solid #090909",
    width: 150,
    textAlign: "right"
  },
  calcWarning: {
    backgroundColor: "#FEEFB3",
    border: "2px solid #9F6000",
    color: "#9F6000"
  },
  calcError: {
    backgroundColor: "red",
    border: "2px solid black",
    color: "white"
  },
  calcSucess: {
    backgroundColor: "#DFF2BF",
    border: "2px solid #548D17",
    color: "#548D17"
  },
  helpIcon: {
    cursor: "pointer",
    marginTop: 14,
    marginRight: 10
  },
  fullWidth: {
    width: "100%"
  },
  explain: {
    width: "100%",
    textAlign: "center",
    color: "red"
  }
}));

export const SummaryContent = ({
  reportID,
  totalRevenue,
  totalExpense,
  totalCash,
  prevCash,
  months,
  year,
  submitChapterFinancial,
  isEditable,
  onRefetch,
  onHelp,
  notes
}) => {
  const classes = useStyles();

  const [isDirty, setDirty] = useState(false);
  const [_notes, setNotes] = useState(notes);

  const handleSubmit = () => {
    (async () => {
      if (reportID) {
        if (months > 9 && _notes === "") {
          //Do nothing
        } else {
          setDirty(true);
          await submitChapterFinancial(reportID, _notes);
          onRefetch();
        }
      }
    })();
  };

  const handleNoteChange = ({ target: { value } }) => {
    setNotes(value);
  };

  const difference = (
    totalCash -
    (totalRevenue - totalExpense + prevCash)
  ).toFixed(2);

  return (
    <React.Fragment>
      <Typography>
        <strong>Summary</strong>
      </Typography>
      <Box mb={1} />
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>Revenue</Typography>
            <Typography>{asCurrency(totalRevenue)}</Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>Less expenses</Typography>
            <Typography className={classes.borderBottom}>
              {asCurrency(-totalExpense)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>FY {year} Net</Typography>
            <Typography>{asCurrency(totalRevenue - totalExpense)}</Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>
              Plus Reported FY {year - 1} cash &amp; investments
            </Typography>
            <Typography className={classes.borderBottom}>
              {asCurrency(prevCash)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>Calculated FY {year} cash &amp; investments</Typography>
            <Typography>
              {asCurrency(totalRevenue - totalExpense + prevCash)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>Reported FY {year} cash &amp; investments</Typography>
            <Typography className={classes.borderBottom}>
              {asCurrency(totalCash)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justify="space-between">
            <Typography>Difference</Typography>
            <Typography>{asCurrency(difference)}</Typography>
          </Grid>
        </Grid>
      </Grid>
      {Boolean(months) && (
        <>
          <Box p={2} mt={2}>
            <Box
              className={
                months >= 1 && months <= 9
                  ? classes.calcSuccess
                  : months >= 10 && months <= 15
                  ? classes.calcWarning
                  : classes.calcError
              }
              p={2}
            >
              <Typography>
                At the end of the FY this chapter has {Math.floor(months)}{" "}
                months worth of expenses in cash and investments.
              </Typography>
            </Box>
          </Box>
          {months > 9 && (
            <>
              <div
                className={classes.explain}
              >{`Please explain why your chapter holds reserves equivalent to ${Math.floor(
                months
              )} months' worth of expenses and share your chapter's plan to spend down its reserves to the recommended limit`}</div>
              <TextField
                label="Notes"
                multiline
                disabled={isEditable === true ? false : true}
                rows={5}
                placeholder={`Please explain why your chapter holds reserves equivalent to ${Math.floor(
                  months
                )} months' worth of expenses and share your chapter's plan to spend down its reserves to the recommended limit`}
                value={_notes}
                onChange={handleNoteChange}
                variant="outlined"
                className={classes.fullWidth}
              />
            </>
          )}
        </>
      )}
      {isEditable && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          {isDirty ? (
            <Loading />
          ) : (
            <>
              <HelpOutline
                className={classes.helpIcon}
                onClick={() =>
                  onHelp(REPORT_ITEM_TYPE.SUMMARY, REPORT_ITEM_SUB_TYPE.SUBMIT)
                }
              />
              <Button
                color="primary"
                onClick={handleSubmit}
                disabled={
                  !(difference === "-0.00" || difference === "0.00") ||
                  (months > 9 &&
                    (_notes === "" || _notes === undefined || _notes === null))
                }
              >
                Submit Report
              </Button>
            </>
          )}
        </Box>
      )}
    </React.Fragment>
  );
};

export default SummaryContent;
